/**
 * AI summary request assembler (#719).
 *
 * Builds the `POST /scans/summarize` body from AI Scanner flow state. Pure —
 * every input is passed in, including `localize`, so this is unit-testable
 * without a React tree or a store.
 *
 * The contract's hard rules, all enforced here:
 *  - every `value` is a STRING (array of strings for `in`) — numbers are a 400
 *  - every scan carries an English `scan` id AND a JP `label`
 *  - no raw Syphon codes go over the wire (`166,0111` → `東証プライム`)
 *  - `matched[]` OMITS scans with no value — never `null`, `0`, or `"N/A"`
 *  - every question needs a non-empty `id` and `question`
 *
 * @see src/constants/summarizeDisplayMap.js for the code→label/unit table.
 */

import HASHTAG_FILTER_CONFIG from '../constants/hashtagFilterConfig';
import INVESTMENT_QUESTIONS from '../constants/investmentQuestions';
import { OPERATOR_MAP, POSITIVE_FORM } from '../constants/summarizeDisplayMap';
import { HASHTAG_DEFINITIONS } from '../views/scan/HashtagChips';
import Logger from './logger';
import { CANDIDATE_COUNT, getRankingScans } from './scanCandidates';
import {
  formatScanValue,
  localizeOrNull,
  scanLabel
} from './scanDisplayValues';

// Re-exported so `summarizePayload` stays the one import for callers already
// building a payload; the implementations live in `scanDisplayValues` because
// candidate selection (#736) needs them too.
export { formatScanValue, scanLabel };

/** Syphon caps `symbols[]` at 5 (`MAX_SYMBOLS`). */
export const MAX_SUMMARY_SYMBOLS = 5;

/**
 * Leading `Q1. ` / `A. ` markers are part of the on-screen label per the Figma
 * design, but they are display chrome — Syphon's sample carries the bare
 * question and option text, so they are stripped before sending.
 */
const QUESTION_PREFIX = /^Q\d+[.．]\s*/;
const OPTION_PREFIX = /^[A-Z][.．]\s*/;

/**
 * The chip labels are stored with a space after the hash (`# 毎日使う身近な企業`)
 * for on-screen spacing, but a hashtag in prose has none — Syphon's own sample
 * uses `#毎日使う身近な企業`. Normalized so the prompt reads naturally.
 */
const TAG_HASH_SPACING = /^#[\s\u3000]+/;

/**
 * The stored/transmitted form of a hashtag label: the chip copy with the space
 * after its 「#」 removed (the chips render 「# 誰もが知る身近な企業」, the payload
 * carries 「#誰もが知る身近な企業」).
 *
 * Exported because STORY-015 (#729) has to go the other way — matching a
 * persisted round's labels back to the chips that produced them — and doing
 * that against the raw copy silently matches nothing.
 *
 * @param {?string} label - A localized chip label.
 * @returns {string} The normalized tag, or '' when there is no label.
 */
export function normalizeHashtagLabel(label) {
  return (label || '').replace(TAG_HASH_SPACING, '#');
}

/**
 * Builds `investmentProfile.questions[]`.
 *
 * `answers` holds option CODES (`'A'|'B'|'C'`); the JP text lives in the string
 * table. A question with no answer is skipped — Syphon requires `answer` to be
 * present, and a half-answered profile should not reach the service.
 */
function buildQuestions(answers, localize) {
  const selected = answers || {};

  return INVESTMENT_QUESTIONS.map((question) => {
    const prompt = localizeOrNull(localize, question.promptKey);
    if (!prompt) return null;

    const options = question.options
      .map((option) => localizeOrNull(localize, option.labelKey))
      .filter(Boolean)
      .map((text) => text.replace(OPTION_PREFIX, ''));

    const answerKey = (
      question.options.find((o) => o.code === selected[question.id]) || {}
    ).labelKey;
    const answerText = localizeOrNull(localize, answerKey);
    if (!answerText) return null;

    return {
      id: question.id,
      question: prompt.replace(QUESTION_PREFIX, ''),
      options: options,
      answer: answerText.replace(OPTION_PREFIX, '')
    };
  }).filter(Boolean);
}

/**
 * Converts one popsicle filter condition to its Syphon display form.
 *
 * Returns null when the condition cannot be represented — an unmapped operator,
 * or a value whose codes have no labels. Dropping is safe: `conditions[]` is
 * descriptive context for the prompt, not a filter Syphon re-executes, so an
 * omitted condition costs the summary one stated reason rather than corrupting
 * the result.
 */
function buildCondition(condition, localize) {
  const { scanName, operator, value } = condition;
  const label = scanLabel(scanName, localize);
  if (!label) return null;

  // Negations with a named positive equivalent (SecurityType → 普通株).
  const positive = POSITIVE_FORM[`${scanName}:${operator}`];
  if (positive) {
    const text = localizeOrNull(localize, positive.valueKey);
    if (!text) return null;
    return {
      scan: scanName,
      label: label,
      operator: positive.operator,
      value: text
    };
  }

  const mapped = OPERATOR_MAP[operator];
  if (!mapped) return null;

  if (mapped === 'in') {
    if (!Array.isArray(value)) return null;
    const labels = value
      .map((code) => formatScanValue(scanName, code, localize))
      .filter(Boolean);
    // De-duplicate: TSE Prime is two codes (`166,0111` / `166,0113`) that both
    // resolve to 東証プライム, and a repeated label reads as noise in the prompt.
    const unique = Array.from(new Set(labels));
    if (unique.length === 0) return null;
    return { scan: scanName, label: label, operator: mapped, value: unique };
  }

  const text = formatScanValue(scanName, value, localize);
  if (!text) return null;
  return { scan: scanName, label: label, operator: mapped, value: text };
}

/**
 * Builds `selectedHashtags[]` — `{ tag, conditions[] }` per selected hashtag,
 * in the order the definitions declare them so the prompt is stable.
 */
function buildHashtags(selectedHashtagIds, localize) {
  const ids =
    selectedHashtagIds instanceof Set
      ? selectedHashtagIds
      : new Set(selectedHashtagIds || []);
  if (ids.size === 0) return [];

  return HASHTAG_DEFINITIONS.filter((definition) => ids.has(definition.id))
    .map((definition) => {
      const tag = localizeOrNull(localize, definition.labelKey);
      const conditions = (HASHTAG_FILTER_CONFIG[definition.id] || [])
        .map((condition) => buildCondition(condition, localize))
        .filter(Boolean);
      if (!tag || conditions.length === 0) return null;
      return { tag: normalizeHashtagLabel(tag), conditions: conditions };
    })
    .filter(Boolean);
}

/**
 * Builds `symbols[]` — up to 5 entries of `{ symbol, name, matched[] }`.
 *
 * `matched[]` covers the scans that explain the symbol's presence AND its
 * placement — the selected hashtags' filter scans plus #736's two ranking
 * scans. Those are precisely the values the summary has to justify.
 *
 * A symbol with no resolvable `name` is dropped rather than sent with an empty
 * one — Syphon 400s on a blank name, and losing one candidate beats losing the
 * whole request.
 */
function buildSymbols(symbolIds, scanNames, scanData, symbolMetadata, localize) {
  const data = scanData || {};
  const metadata = symbolMetadata || {};

  return (symbolIds || [])
    .slice(0, MAX_SUMMARY_SYMBOLS)
    .map((symbolId) => {
      // `state.symbols.data[id]` wraps the payload in `.data`; accept the
      // unwrapped shape too so tests can pass a plain map.
      const entry = metadata[symbolId];
      const meta = (entry && entry.data) || entry;
      const name = meta && (meta.jpShortName || meta.name);

      if (!name) {
        // Syft does not always resolve a symbol — an unmatched entry never gets
        // `receiveSymbolMetaData` and stays loading forever. A bare ticker is
        // not a name a member would recognise, so the candidate is skipped
        // outright rather than renamed or backfilled.
        Logger.error({
          errorCode: 'E0046',
          message: `Scan summary: skipping ${symbolId} — no name resolved`
        });
        return null;
      }

      const matched = scanNames
        .map((scanName) => {
          const scanEntry = (data[scanName.toLowerCase()] || {}).symbols;
          const symbolHit = scanEntry && scanEntry[symbolId];
          const raw = symbolHit && symbolHit.filterValue;
          const value = formatScanValue(scanName, raw, localize, true);
          if (!value) return null;
          const label = scanLabel(scanName, localize);
          if (!label) return null;
          return { scan: scanName, label: label, value: value };
        })
        .filter(Boolean);

      if (matched.length === 0) return null;
      return { symbol: symbolId, name: String(name).trim(), matched: matched };
    })
    .filter(Boolean);
}

/**
 * Assembles the full `POST /scans/summarize` request body.
 *
 * @param {object} params
 * @param {string} params.sessionId - Echoed back by Syphon in `data.sessionId`.
 * @param {{q1:?string,q2:?string}} params.answers - Option codes from state.
 * @param {Set<string>|string[]} params.selectedHashtagIds
 * @param {string[]} params.symbols - Candidate symbol IDs, best-first — #736's
 *   composite ranking, not the pool order. Only the first
 *   {@link MAX_SUMMARY_SYMBOLS} are considered; one with no resolved name is
 *   skipped and NOT backfilled — a lower-ranked symbol must not take a top
 *   slot, so a short request is the honest outcome and the E0046 log says
 *   which symbol was lost.
 * @param {object} params.scanData - `state.scans.scanData`.
 * @param {object} params.symbolMetadata - `state.symbols` metadata by symbol ID.
 * @param {Function} params.localize - `useLocalize()`'s localize.
 * @returns {object|null} The request body, or null when it would be invalid
 *   (no answers, no hashtags, or no sendable symbol).
 */
export function buildSummarizeRequest({
  sessionId,
  answers,
  selectedHashtagIds,
  symbols,
  scanData,
  symbolMetadata,
  localize
}) {
  if (!sessionId) return null;

  const questions = buildQuestions(answers, localize);
  if (questions.length !== INVESTMENT_QUESTIONS.length) return null;

  const selectedHashtags = buildHashtags(selectedHashtagIds, localize);
  if (selectedHashtags.length === 0) return null;

  // The scans that explain each symbol, de-duplicated in declaration order:
  // the ones the hashtag selection FILTERED on, then the two #736 RANKED on.
  //
  // Both belong in `matched[]` and answer different halves of "why this
  // stock?" — the hashtag scans say why it is in the pool at all, the ranking
  // scans say why it beat the rest of that pool to a top-five slot. Sending
  // only the first left the summary unable to justify the ordering it was
  // describing. Ranking scans come last so a member reading the summary meets
  // the filter reasons in the order they chose them.
  const scanNames = Array.from(
    new Set(
      selectedHashtags
        .reduce(
          (acc, hashtag) =>
            acc.concat(hashtag.conditions.map((condition) => condition.scan)),
          []
        )
        .concat(getRankingScans(answers))
    )
  );

  const built = buildSymbols(
    symbols,
    scanNames,
    scanData,
    symbolMetadata,
    localize
  );
  if (built.length === 0) return null;

  return {
    sessionId: sessionId,
    investmentProfile: { questions: questions },
    selectedHashtags: selectedHashtags,
    symbols: built
  };
}

/**
 * STORY (#724): build the persisted `SearchRound` (minus `aiSummary`) from a
 * summarize request `body`, so the stored hashtags/results always match what
 * was actually sent to the summarizer. Centralizes the round shape in one place
 * with `buildSummarizeRequest`, so the two cannot drift.
 *
 * `body.symbols` is ALREADY the ranked candidate list: #736 ranks the pool
 * before the request is assembled, so the order here is the order the member
 * sees, and the round persists it as-is. Capped to `CANDIDATE_COUNT` rather
 * than re-running `selectCandidates` — re-ranking an already-ranked list would
 * need the scan data the body no longer carries, and would reorder the round
 * against the very summary it is stored with. The cap still earns its keep:
 * `MAX_SUMMARY_SYMBOLS` and `CANDIDATE_COUNT` are equal today, so it is a
 * no-op, but it is what keeps a restored round's "N 件見つかりました！" matching
 * what the member saw if they ever diverge.
 *
 * STORY-024 (#734): `jpIndustry` and `shortDescription` are persisted alongside
 * the name for the same reason the name is — a restored round must render them
 * without reading `state.symbols.data`, which #725's FR-009 forbids and which
 * is empty anyway once the app has been reopened. They come in as a separate
 * map rather than riding on `body.symbols`, because `body` is the outbound
 * Syphon request and the summarizer has no use for either.
 *
 * Persisted as `null` when unknown, and rounds written before this field
 * existed simply have no key — both land on the card's empty industry slot,
 * which is the same thing the AC asks for when a symbol has no industry. That
 * is deliberately NOT a `SCHEMA_VERSION` bump: `load()` discards a session
 * whose version does not match, so bumping would throw away every member's
 * saved history to add a cosmetic tag.
 *
 * @param {object|null} body - Output of {@link buildSummarizeRequest}; `null`
 *   when the request could not be assembled (returns `null`).
 * @param {{q1:?string,q2:?string}} answers - Q1/Q2 option codes.
 * @param {string} conversationId - The in-memory conversation id.
 * @param {number|null} matchCount - The hashtag match count for this round.
 * @param {Object<string, {jpIndustry: ?string, shortDescription: string}>}
 *   [candidatesBySymbol] - Ranked candidates by symbol id, from
 *   `selectCandidates`, supplying each result's Syft industry CODE and company
 *   description. Supplemental: it can only fill those fields, never reorder or
 *   drop a result, so the round still matches what was sent even when absent.
 * @param {Iterable<string>} [selectedHashtagIds] - STORY-015 (#729): the chip
 *   ids behind `body.selectedHashtags`, stored so a resumed round can be
 *   searched again on the same hashtags.
 * @returns {object|null} The `SearchRound`, or `null` when `body` is null.
 */
export function buildSearchRound(
  body,
  answers,
  conversationId,
  matchCount,
  candidatesBySymbol,
  selectedHashtagIds
) {
  if (!body) return null;
  const candidates = candidatesBySymbol || {};
  return {
    selectedHashtags: body.selectedHashtags.map(({ tag }) => ({ tag })),
    // STORY-015 (#729): the CHIP IDS behind those labels. `selectedHashtags`
    // stores localized display text, which cannot be turned back into a
    // selection — so without this a resumed session can render its history but
    // never search again on the same hashtags, and Scenario B
    // (「いいえ」 → new Q1/Q2 → new request) has nothing to search with.
    // Absent on rounds persisted before this field existed; callers treat that
    // as an empty selection rather than failing to restore the round.
    selectedHashtagIds: Array.from(selectedHashtagIds || []),
    matchCount: matchCount,
    q1Answer: (answers && answers.q1) || null,
    q2Answer: (answers && answers.q2) || null,
    aiSummary: null,
    results: body.symbols.slice(0, CANDIDATE_COUNT).map((entry) => {
      const candidate = candidates[entry.symbol] || {};
      return {
        symbol: entry.symbol,
        companyName: entry.name,
        shortDescription: candidate.shortDescription || '',
        jpIndustry: candidate.jpIndustry || null
      };
    }),
    conversationId: conversationId
  };
}

export default buildSummarizeRequest;
